import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import styled from '@emotion/styled';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { FrameIndex, Frames, Milliseconds, OverlayDraggableData, OverlayItem } from '@/redux/slices/packagingSlice.types';
import { DRAGGABLE_TYPES } from '@/redux/slices/packagingSlice.types';
import { isOverlayWithAssignedVideo, isAssignedOverlay } from '@/components/Packaging/typeGuards';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { removeOverlay as removeOverlayAction, toggleOverlayLock } from '@/redux/slices/packagingTimelineSlice';
import { setActiveCutType, setActiveHover, clearActiveHover } from '@/redux/slices/packagingSlice';
import { getOptionColor, getOverlayWidth, type IsOverlayFitResult } from '@/components/Packaging/utils';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import {
  MILLISECONDS_PER_PIXEL,
  RESIZE_HANDLE_WIDTH,
  UNASSIGNED_OVERLAY_COLOR_DARK_THEME,
  UNASSIGNED_OVERLAY_COLOR_LIGHT_THEME,
  UNASSIGNED_SEGMENT_BORDER_COLOR_DARK_THEME,
  UNASSIGNED_SEGMENT_BORDER_COLOR_LIGHT_THEME,
  UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_DARK_THEME,
  UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_LIGHT_THEME,
  UNASSIGNED_SEGMENT_GRIP_COLOR_DARK_THEME,
  UNASSIGNED_SEGMENT_GRIP_COLOR_LIGHT_THEME
} from '@/components/Packaging/constants';
import {
  selectIsReadOnlyPackaging,
  selectMovingOverlayNewPosition,
  selectPackagingTimelineFrameDuration,
  selectPackagingTimelineScale
} from '@/redux/selectors/packagingSelectors';
import { selectOutsideTimelineOverlaysOrdered, selectTimelineFramesCount } from '@/redux/selectors/packagingTimelineSelectors';
import { ResizeHandle } from '@/components/Packaging/LayeredTimeline/overlays/ResizeHandle';
import Toolbar from '@/components/Packaging/LayeredTimeline/common/Toolbar';
import GripIcon from '@/components/Packaging/LayeredTimeline/common/GripIcon';
import StartEndIndicator from '@/components/Packaging/LayeredTimeline/overlays/StartEndIndicator';
import { useResizableOverlay } from '@/components/Packaging/LayeredTimeline/overlays/useResizableOverlay.hook';
import DurationEdit from '@/components/Packaging/LayeredTimeline/overlays/duration/DurationEdit';
import DurationView from '@/components/Packaging/LayeredTimeline/overlays/duration/DurationView';
import UnlockButton from '@/components/Packaging/LayeredTimeline/common/UnlockButton';

interface OverlayProps {
  overlay: OverlayItem;
  layerIndex: number;
  isFit: IsOverlayFitResult;
}

const Overlay: React.FC<OverlayProps> = ({ overlay, layerIndex, isFit }) => {
  const dispatch = useAppDispatch();
  const scale = useAppSelector(selectPackagingTimelineScale);
  const frameDuration = useAppSelector(selectPackagingTimelineFrameDuration);
  const timelineFramesCount = useAppSelector(selectTimelineFramesCount);
  const movingPosition = useAppSelector(selectMovingOverlayNewPosition);
  const color = getOptionColor(overlay.kind);
  const wrapperRef = useRef<HTMLElement | null>(null);
  const startEndIndicatorPlacementRef = useRef<'left' | 'right'>('left');
  const { isLocked } = overlay;
  const [isToolbarOpen, setIsToolbarOpen] = useState(false);
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const isOutsideTimeline = isFit.outsideTimeline; // needed to highlight overlay with different color
  const isFullyOutsideTimeline = overlay.start >= timelineFramesCount; // needed to find out if we need calculate a different rendering position
  // `outsideTimelineOverlays` needed to calculate the rendering position of this overlay
  const outsideTimelineOverlays = useAppSelector((state) => selectOutsideTimelineOverlaysOrdered(state, !isFullyOutsideTimeline, layerIndex));
  // `outsideTimelinePosition` is the position of the overlay when any part of it is reaching outside the boundaries of the timeline.
  // When overlay is inside the timeline `outsideTimelinePosition` is null.
  let outsideTimelinePosition: FrameIndex | null = null;
  const isReadOnly = useAppSelector(selectIsReadOnlyPackaging);

  const theme = useTheme();
  const hasAssignedInventory = isAssignedOverlay(overlay);
  const isDarkTheme = theme.palette.mode === 'dark';
  const gripIconColor = useMemo(() => {
    if (hasAssignedInventory && !isLocked) {
      return color;
    }
    return isDarkTheme ? UNASSIGNED_SEGMENT_GRIP_COLOR_DARK_THEME : UNASSIGNED_SEGMENT_GRIP_COLOR_LIGHT_THEME;
  }, [hasAssignedInventory, isLocked, color, isDarkTheme]);

  // The duration of overlay with assigned video is defined by the video duration, so we don't want users to be able to resize it.
  const canChangeDuration = !isOverlayWithAssignedVideo(overlay);

  const { isResizing, endPositionInternal, handleResizeStart, handleResize, handleResizeEnd } = useResizableOverlay({
    overlay,
    layerIndex,
    isOutsideTimeline
  });

  const start: FrameIndex = movingPosition !== null ? movingPosition.start : overlay.start;
  const framesCount: Frames = (endPositionInternal !== null ? endPositionInternal : overlay.end) - overlay.start;

  let end: FrameIndex;
  if (movingPosition !== null) {
    end = movingPosition.end;
  } else if (endPositionInternal !== null) {
    end = endPositionInternal;
  } else {
    end = overlay.end;
  }

  const data: OverlayDraggableData = {
    type: DRAGGABLE_TYPES.OVERLAY,
    kind: overlay.kind,
    layerIndex
  };

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: overlay.draggableId,
    data,
    disabled: isReadOnly
  });

  // calculate the `outsideTimelinePosition`
  if (isFullyOutsideTimeline && outsideTimelineOverlays) {
    const thisOverlayIndex = outsideTimelineOverlays.findIndex((o) => o.draggableId === overlay.draggableId);
    if (thisOverlayIndex === 0) {
      outsideTimelinePosition = timelineFramesCount;
    } else {
      const first = outsideTimelineOverlays[0];
      const isFirstFullyOutsideTimeline = first.start >= timelineFramesCount;
      const prevOverlaysDuration = outsideTimelineOverlays.slice(0, thisOverlayIndex).reduce((acc, o) => acc + (o.end - o.start), 0);
      outsideTimelinePosition = timelineFramesCount + prevOverlaysDuration - (isFirstFullyOutsideTimeline ? 0 : timelineFramesCount - first.start);
    }
  }

  if (isDragging) {
    startEndIndicatorPlacementRef.current = 'left';
  } else if (isResizing) {
    startEndIndicatorPlacementRef.current = 'right';
  }

  const width = getOverlayWidth(framesCount, frameDuration, scale);
  const renderedPosition: Milliseconds =
    (isFullyOutsideTimeline && outsideTimelinePosition ? outsideTimelinePosition : overlay.start) * frameDuration;

  const style = {
    transform: CSS.Translate.toString(transform),
    cursor: 'default',
    zIndex: isDragging ? 2 : 1,
    width: `${width}px`,
    left: `${(renderedPosition * scale) / MILLISECONDS_PER_PIXEL}px`
  };

  style.cursor = isReadOnly ? 'default' : isDragging ? 'grabbing' : 'grab';

  const removeOverlay = () =>
    dispatch(
      removeOverlayAction({
        layerIndex,
        overlayId: overlay.draggableId
      })
    );

  const handleRemoveButtonClick = () => {
    removeOverlay();
  };

  const handleAssignInventoryButtonClick = () => {
    dispatch(setActiveCutType(overlay));
  };

  const refCombined = (node: HTMLElement | null) => {
    setNodeRef(node);
    wrapperRef.current = node;
  };

  const handleMouseEnter = () => {
    setIsToolbarOpen(true);
    dispatch(setActiveHover([overlay]));
  };

  const handleMouseLeave = () => {
    setIsToolbarOpen(false);
    dispatch(clearActiveHover());
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
    if (['Delete', 'Backspace'].includes(event.key) && !isLocked) {
      removeOverlay();
    } else if (listeners?.onKeyDown) {
      listeners.onKeyDown(event);
    }
  };

  const toggleLock = () => {
    dispatch(toggleOverlayLock(overlay.draggableId));
  };

  const editDuration = useCallback(() => setIsEditingDuration(true), []);
  const finishEditDuration = useCallback(() => setIsEditingDuration(false), []);

  const isToolbarInPopper = width < 133;

  return (
    <Wrapper
      ref={refCombined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={style}
      color={color}
      isLocked={isLocked}
      isInvalid={!isFit.fit}
      hasAssignedInventory={hasAssignedInventory}
      {...attributes}
      data-prevent-timeline-seek
      data-overlay-id={overlay.draggableId}
      /* order of props below matters */
      {...listeners}
      onKeyDown={handleKeyDown}
    >
      {wrapperRef.current && (
        <StartEndIndicator
          isOpen={isDragging || isResizing}
          overlayElement={wrapperRef.current}
          start={start}
          end={end}
          placement={startEndIndicatorPlacementRef.current}
        />
      )}
      {width > 38 && !isReadOnly && <GripIcon color={gripIconColor} />}
      {!isLocked && !isReadOnly && (
        <ToolbarWrapper isInPopper={isToolbarInPopper}>
          <Toolbar
            isInPopper={isToolbarInPopper}
            isPopperOpen={isToolbarOpen && !isDragging && !isResizing}
            segmentWrapperElement={wrapperRef.current}
            hasAssignedInventory={hasAssignedInventory}
            onAssignInventoryButtonClick={!isLocked ? handleAssignInventoryButtonClick : undefined}
            isLocked={isLocked}
            onLockButtonClick={toggleLock}
            color={color}
            onRemoveButtonClick={handleRemoveButtonClick}
          />
        </ToolbarWrapper>
      )}
      <Clip>
        {width > 125 && (
          <Label hasAssignedInventory={hasAssignedInventory}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <StyledLayerIcon icon="layers" height={15} width={15} />
              {width > 140 && <Title>{overlay.label}</Title>}
            </div>
            {width < 56 ? null : <Subtitle locked={isLocked}>{overlay.subtitle}</Subtitle>}
          </Label>
        )}
        {width > 100 &&
          (isEditingDuration ? (
            <DurationEditStyled
              color={color}
              overlay={overlay}
              layerIndex={layerIndex}
              hasAssignedInventory={hasAssignedInventory}
              finishEdit={finishEditDuration}
            />
          ) : (
            <DurationViewStyled
              isLocked={isLocked}
              color={color}
              duration={framesCount}
              hideEditButton={isOutsideTimeline || isLocked || !canChangeDuration}
              hasAssignedInventory={hasAssignedInventory}
              onEditButtonClick={editDuration}
            />
          ))}
        {isLocked && <UnlockButtonStyled color={color} hasAssignedInventory={hasAssignedInventory} onClick={toggleLock} />}
      </Clip>
      {canChangeDuration && !isLocked && !isReadOnly && (
        <ResizeHandle
          color={color}
          isRoundLeftBorders={width < RESIZE_HANDLE_WIDTH + 2}
          hasAssignedInventory={hasAssignedInventory}
          onResizeStart={handleResizeStart}
          onResize={handleResize}
          onResizeEnd={handleResizeEnd}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(Overlay);

const Wrapper = styled.div<{ color: string; isInvalid: boolean; isLocked: boolean; hasAssignedInventory: boolean }>`
  position: absolute;
  z-index: 1;
  height: 55px;
  box-sizing: border-box;
  background: ${({ isLocked, isInvalid, color, hasAssignedInventory, theme }) => {
    if (isInvalid) return '#ff00005a';
    if (!hasAssignedInventory) return 'transparent';
    if (isLocked) return `${color}3a`;
    return theme.palette.mode === 'dark' ? '#191D24d4' : `${color}40`;
  }};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border-width: 1px;
  border-style: ${({ hasAssignedInventory }) => (hasAssignedInventory ? 'solid' : 'dashed')};
  border-color: ${({ color, isInvalid, hasAssignedInventory, theme }) => {
    if (isInvalid) return '#ff00005a';
    if (!hasAssignedInventory)
      return theme.palette.mode === 'dark' ? UNASSIGNED_SEGMENT_BORDER_COLOR_DARK_THEME : UNASSIGNED_SEGMENT_BORDER_COLOR_LIGHT_THEME;
    return color;
  }};
  user-select: none;

  &:focus {
    box-shadow: inset 0 0 5px
      ${({ theme, color, hasAssignedInventory }) => {
        const isDarkTheme = theme.palette.mode === 'dark';

        if (!hasAssignedInventory) {
          return isDarkTheme ? UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_DARK_THEME : UNASSIGNED_SEGMENT_BOX_SHADOW_COLOR_LIGHT_THEME;
        }

        return `${color}${isDarkTheme ? 'ff' : '66'}`;
      }};
  }
`;

const ToolbarWrapper = styled.div<{ isInPopper: boolean }>`
  position: absolute;
  z-index: 1;
  ${({ isInPopper, theme }) => {
    return isInPopper ? '' : `right: ${theme.spacing(2)};`;
  }}
`;

const Clip = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.shape.borderRadius - 1}px;
`;

const Label = styled.div<{ hasAssignedInventory: boolean }>`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing(0.25)};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(0, 12.5, 0, 1)};
  white-space: nowrap;
  ${({ hasAssignedInventory }) => (hasAssignedInventory ? undefined : 'opacity: 0.7;')}
`;

const Title = styled.div`
  width: 100%;
  font-size: 0.77rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledLayerIcon = styled(PolarisIcon)`
  margin-right: ${({ theme }) => theme.spacing(0.75)};
  flex-shrink: 0;
`;

const DurationEditStyled = styled(DurationEdit)`
  right: ${({ theme }) => theme.spacing(2)};
`;

const DurationViewStyled = styled(DurationView)<{ isLocked: boolean }>`
  right: ${({ theme, isLocked }) => theme.spacing(isLocked ? 0.75 : 2)};
`;

const UnlockButtonStyled = styled(UnlockButton)<{ hasAssignedInventory: boolean }>`
  top: ${({ theme }) => theme.spacing(0.25)};
  right: ${({ theme }) => theme.spacing(0.25)};
  ${({ theme, hasAssignedInventory }) => {
    if (!hasAssignedInventory) {
      const isDarkTheme = theme.palette.mode === 'dark';
      return `color: ${isDarkTheme ? UNASSIGNED_OVERLAY_COLOR_DARK_THEME : UNASSIGNED_OVERLAY_COLOR_LIGHT_THEME};`;
    }

    return undefined; // when inventory is assigned use styles that declared in UnlockButton internally
  }}
`;

const Subtitle = styled.div<{ locked: boolean }>`
  width: 100%;
  font-size: 0.9rem;
  font-weight: 400;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  opacity: 0.66;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ color }) => color};
`;
